// Raio-X do frete: o que uma oferta de frete paga perto do custo da viagem e
// do piso da ANTT, quanto pedir e a mensagem para o WhatsApp. Sem IA: só as
// contas com o que a empresa já lança no app.
//
// O custo da viagem (corrigido com o titular em 11/10/2026) é só o que a
// viagem gasta, sem o histórico de manutenção do caminhão: um conserto de
// motor ou meio jogo de pneus no mês não pode fazer qualquer frete parecer
// prejuízo.
//   diesel     = km rodados ÷ consumo × preço do litro (o consumo do caminhão
//                nos últimos 6 meses, ou o da frota; o preço médio pago nos
//                últimos 30 dias)
//   despesas   = km rodados × a média por km da frota nos últimos 12 meses do
//                grupo "viagem" sem o combustível (pedágio, Arla, alimentação,
//                pernoite, chapa, balança, estacionamento)
//   reserva    = km rodados × o valor por km que a empresa digita para a
//                manutenção e os pneus
//   motorista  = a remuneração e o agenciamento, em % do frete (o caminhão nos
//                últimos meses, ou a frota)
// Os custos fixos (seguro, IPVA, parcelas, rastreador) e as despesas gerais não
// entram: saem da sobra.
import { pisoMinimo, VIGENCIA_DO_PISO } from './antt.js';
import { abastecimentosDoVeiculo, consumoNoPeriodo, leiturasDoVeiculo, relatorioDosCaminhoes } from './custos.js';
import { somarDias } from './despesas.js';
import { dinheiro, numeroBR } from './formato.js';
import { aprendidoDasViagens } from './modo-viagem.js';

const reais = (v) => Math.round(v * 100) / 100;
const centavos = (v) => Math.round((Number(v) || 0) * 100);
const vivo = (x) => !x.deleted_at;

export const CUSTO_DA_VIAGEM = {
  diasDoConsumo: 183, // os últimos 6 meses
  kmMinimo: 1000, // menos que isso não dá uma média confiável
  kmPorLitro: { min: 0.8, max: 8 }, // fora disso, é erro de digitação
  diasDoPreco: 30,
  ultimosAbastecimentos: 3, // sem abastecimento nos 30 dias, a média dos últimos
  precoDoLitro: { min: 3, max: 15 },
  diasDasDespesas: 365, // os últimos 12 meses
  kmPorDia: 1500, // mais que isso entre duas leituras, é erro de digitação
  mesesDoMotorista: 3, // os 3 meses antes do atual e o atual
  motoristaMaximo: 0.6,
};

// "São João d'Aliança" → "SAO JOAO DALIANCA" (igual a app.chave_do_nome() no banco)
export function chaveDoNome(nome) {
  return String(nome ?? '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/['’`´]/g, '')
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, ' ')
    .trim();
}

const idsDoDiesel = (categorias) => new Set((categorias ?? []).filter((c) => c.exige_hodometro).map((c) => c.id));
const tratores = (tabelas) => (tabelas.veiculos ?? []).filter((v) => vivo(v) && v.tipo === 'trator');

function consumoAceito(c) {
  if (!c || c.km < CUSTO_DA_VIAGEM.kmMinimo) return null;
  const { min, max } = CUSTO_DA_VIAGEM.kmPorLitro;
  return c.kmPorLitro >= min && c.kmPorLitro <= max ? c : null;
}

// Consumo (km/l) de um caminhão nos últimos 6 meses, de tanque cheio a tanque
// cheio (o mesmo jeito do relatório dos caminhões). null: poucos km ainda.
export function consumoDoCaminhao(tabelas, veiculoId, { hoje }) {
  const de = somarDias(hoje, -CUSTO_DA_VIAGEM.diasDoConsumo);
  const abastecimentos = abastecimentosDoVeiculo(veiculoId, tabelas.lancamentos ?? [], idsDoDiesel(tabelas.categorias));
  return consumoAceito(consumoNoPeriodo(abastecimentos, { de, ate: hoje }));
}

// O consumo da frota: os km e os litros de todos os caminhões somados.
export function consumoDaFrota(tabelas, { hoje }) {
  let km = 0;
  let litros = 0;
  for (const v of tratores(tabelas)) {
    const c = consumoDoCaminhao(tabelas, v.id, { hoje });
    if (!c) continue;
    km += c.km;
    litros += c.litros;
  }
  return km >= CUSTO_DA_VIAGEM.kmMinimo && litros > 0 ? { km, litros, kmPorLitro: km / litros } : null;
}

// Preço do litro do diesel: a média paga nos últimos 30 dias; sem
// abastecimento nesse tempo, a dos últimos 3; sem nenhum, o da tabela da ANTT.
export function precoDoDiesel(tabelas, { hoje }) {
  const ids = idsDoDiesel(tabelas.categorias);
  const { min, max } = CUSTO_DA_VIAGEM.precoDoLitro;
  const lista = (tabelas.lancamentos ?? [])
    .filter((l) => vivo(l) && l.tipo === 'despesa' && ids.has(l.categoria_id) && l.data <= hoje
      && Number(l.litros) > 0 && Number(l.valor) / Number(l.litros) >= min && Number(l.valor) / Number(l.litros) <= max)
    .sort((a, b) => String(b.data).localeCompare(String(a.data)));
  const media = (xs) => xs.reduce((s, l) => s + Number(l.valor), 0) / xs.reduce((s, l) => s + Number(l.litros), 0);
  const desde = somarDias(hoje, -CUSTO_DA_VIAGEM.diasDoPreco);
  const recentes = lista.filter((l) => l.data >= desde);
  if (recentes.length > 0) return { preco: media(recentes), fonte: 'recentes', abastecimentos: recentes.length };
  if (lista.length > 0) {
    const ultimos = lista.slice(0, CUSTO_DA_VIAGEM.ultimosAbastecimentos);
    return { preco: media(ultimos), fonte: 'ultimos', abastecimentos: ultimos.length, desde: ultimos.at(-1).data };
  }
  return { preco: VIGENCIA_DO_PISO.diesel, fonte: 'antt' };
}

const DESPESAS_DO_PEDAGIO = new Set(['pedagio', 'tag_pedagio']);
const DESPESAS_DO_DIA = new Set(['alimentacao', 'pernoite', 'estacionamento']);

// O caminhão de uma despesa: o dela, o trator da carreta, ou o da viagem.
function caminhaoDaDespesa(l, veiculoPorId, viagemPorId) {
  if (l.veiculo_id) {
    const v = veiculoPorId.get(l.veiculo_id);
    if (v?.tipo === 'implemento') return v.acoplado_a ?? null;
    return l.veiculo_id;
  }
  if (l.viagem_id) return viagemPorId.get(l.viagem_id)?.veiculo_id ?? null;
  return null;
}

// As despesas por km da frota nos últimos 12 meses: as da viagem (o grupo
// "viagem" sem o combustível; em partes, o pedágio, as do dia — alimentação,
// pernoite e estacionamento — e as outras, para o previsto do modo viagem) e,
// só como referência para a reserva, as de manutenção e pneus. Cada caminhão entra com os km entre a primeira e a última
// leitura do hodômetro do período e com as despesas dele entre essas datas; as
// despesas sem caminhão, entre a primeira e a última leitura da frota.
// null: menos de 1.000 km com leitura.
export function despesasPorKm(tabelas, { hoje }) {
  const de = somarDias(hoje, -CUSTO_DA_VIAGEM.diasDasDespesas);
  const janelas = new Map();
  let km = 0;
  let inicio = null;
  let fim = null;
  for (const v of tratores(tabelas)) {
    const leituras = leiturasDoVeiculo(v.id, tabelas).filter((l) => l.data >= de && l.data <= hoje);
    if (leituras.length < 2) continue;
    const primeira = leituras[0].data;
    const ultima = leituras.at(-1).data;
    const kmDele = Math.max(...leituras.map((l) => l.km)) - Math.min(...leituras.map((l) => l.km));
    const dias = (Date.parse(ultima) - Date.parse(primeira)) / 86400000 + 1;
    if (!(kmDele > 0) || kmDele / dias > CUSTO_DA_VIAGEM.kmPorDia) continue;
    janelas.set(v.id, { de: primeira, ate: ultima });
    km += kmDele;
    if (!inicio || primeira < inicio) inicio = primeira;
    if (!fim || ultima > fim) fim = ultima;
  }
  if (km < CUSTO_DA_VIAGEM.kmMinimo) return null;
  const veiculoPorId = new Map((tabelas.veiculos ?? []).map((v) => [v.id, v]));
  const viagemPorId = new Map((tabelas.viagens ?? []).filter(vivo).map((v) => [v.id, v]));
  const categoriaPorId = new Map((tabelas.categorias ?? []).map((c) => [c.id, c]));
  const ids = idsDoDiesel(tabelas.categorias);
  const soma = { viagem: 0, manutencao: 0 };
  const partes = { pedagio: 0, porDia: 0, outras: 0 };
  for (const l of tabelas.lancamentos ?? []) {
    if (!vivo(l) || l.tipo !== 'despesa' || ids.has(l.categoria_id)) continue;
    const cat = categoriaPorId.get(l.categoria_id);
    if (!cat || cat.entra_custo_km === false || !(cat.grupo in soma)) continue;
    const caminhao = caminhaoDaDespesa(l, veiculoPorId, viagemPorId);
    const janela = caminhao ? janelas.get(caminhao) : { de: inicio, ate: fim };
    if (!janela || l.data < janela.de || l.data > janela.ate) continue;
    soma[cat.grupo] += centavos(l.valor);
    if (cat.grupo === 'viagem') partes[DESPESAS_DO_PEDAGIO.has(cat.codigo) ? 'pedagio' : DESPESAS_DO_DIA.has(cat.codigo) ? 'porDia' : 'outras'] += centavos(l.valor);
  }
  return {
    km, de: inicio, ate: fim, viagem: soma.viagem / 100 / km, manutencao: soma.manutencao / 100 / km,
    partes: { pedagio: partes.pedagio / 100 / km, porDia: partes.porDia / 100 / km, outras: partes.outras / 100 / km },
  };
}

// Os 3 meses antes do atual e o atual (a remuneração do motorista).
export function periodoDoMotorista(hoje) {
  const [a, m] = hoje.split('-').map(Number);
  return { de: new Date(Date.UTC(a, m - 1 - CUSTO_DA_VIAGEM.mesesDoMotorista, 1)).toISOString().slice(0, 10), ate: hoje };
}

// O motorista e o agenciamento em % do frete, do relatório dos caminhões: o
// que o caminhão pagou no período, ou a frota. Sem cargas, 0.
export function motoristaDoFrete(relatorio, veiculoId) {
  const pct = (lista) => {
    const fretes = lista.reduce((s, c) => s + (c.fretes ?? 0), 0);
    if (!(fretes > 0)) return null;
    const pago = lista.reduce((s, c) => s + (c.remuneracao?.total ?? 0) + (c.agenciamentos ?? 0), 0);
    return Math.min(pago / fretes, CUSTO_DA_VIAGEM.motoristaMaximo);
  };
  const caminhoes = relatorio?.caminhoes ?? [];
  const doCaminhao = veiculoId ? pct(caminhoes.filter((c) => c.veiculo.id === veiculoId)) : null;
  if (doCaminhao !== null) return { pct: doCaminhao, fonte: 'caminhao' };
  const daFrota = pct(caminhoes);
  return daFrota !== null ? { pct: daFrota, fonte: 'frota' } : { pct: 0, fonte: 'sem' };
}

// Os números da empresa que o Raio-X usa (0.20.0): o consumo da frota e de
// cada caminhão, o preço do diesel, as despesas da viagem por km (e a de
// manutenção, só de referência) e a parte do motorista e do agenciamento.
// O escritório e a rotina diária guardam isto no banco (raiox_custos): o
// celular do motorista e do agenciador só lê e faz a conta.
//   regraValendo e creditosDaViagem: de lib/acerto.js (o relatório precisa)
export function numerosDoRaioX(tabelas, { hoje, regraValendo, creditosDaViagem }) {
  const relatorio = relatorioDosCaminhoes(tabelas, { ...periodoDoMotorista(hoje), hoje, rateio: 'nenhum', regraValendo, creditosDaViagem });
  const caminhoes = {};
  for (const v of tratores(tabelas)) {
    caminhoes[v.id] = { consumo: consumoDoCaminhao(tabelas, v.id, { hoje }), motorista: motoristaDoFrete(relatorio, v.id) };
  }
  return {
    versao: 1,
    hoje,
    frota: { consumo: consumoDaFrota(tabelas, { hoje }), motorista: motoristaDoFrete(relatorio, null) },
    caminhoes,
    diesel: precoDoDiesel(tabelas, { hoje }),
    despesas: despesasPorKm(tabelas, { hoje }),
    // O que as viagens encerradas ensinaram (modo viagem, 0.23.0): o ritmo e as esperas.
    aprendido: aprendidoDasViagens(tabelas, { hoje }),
  };
}

// O custo da viagem com os números da empresa e o que a pessoa digitou.
//   veiculoId: o caminhão (sem números dele, vale a frota)
//   reserva: R$ por km para manutenção e pneus · consumo (km/l) e diesel
//   (R$ o litro): o que a pessoa digitou, ou null para usar os números
// porKm fica null sem o consumo (nem do caminhão, nem da frota, nem digitado).
export function custoDosNumeros(numeros, { veiculoId = null, reserva = 0, consumo = null, diesel = null } = {}) {
  const n = numeros ?? {};
  const doCaminhao = veiculoId ? n.caminhoes?.[veiculoId] : null;
  let kmPorLitro = null;
  if (consumo > 0) kmPorLitro = { kmPorLitro: consumo, fonte: 'digitado' };
  else if (doCaminhao?.consumo) kmPorLitro = { ...doCaminhao.consumo, fonte: 'caminhao' };
  else if (n.frota?.consumo) kmPorLitro = { ...n.frota.consumo, fonte: 'frota' };
  const preco = diesel > 0 ? { preco: diesel, fonte: 'digitado' } : (n.diesel ?? { preco: VIGENCIA_DO_PISO.diesel, fonte: 'antt' });
  const despesas = n.despesas ?? null;
  const motorista = doCaminhao?.motorista?.fonte === 'caminhao' ? doCaminhao.motorista : (n.frota?.motorista ?? { pct: 0, fonte: 'sem' });
  const valorDaReserva = Number(reserva) > 0 ? Number(reserva) : 0;
  return {
    consumo: kmPorLitro,
    diesel: preco,
    despesas,
    reserva: valorDaReserva,
    motorista,
    porKm: kmPorLitro
      ? { diesel: preco.preco / kmPorLitro.kmPorLitro, viagem: despesas?.viagem ?? 0, reserva: valorDaReserva }
      : null,
    pctDoFrete: motorista.pct,
  };
}

// O custo da viagem direto das tabelas da empresa (o app do escritório).
export function custoDaViagem(tabelas, { hoje, regraValendo, creditosDaViagem, ...opcoes }) {
  return custoDosNumeros(numerosDoRaioX(tabelas, { hoje, regraValendo, creditosDaViagem }), opcoes);
}

// Arredonda o valor sugerido para cima, de 10 em 10 reais.
const paraCima = (v) => Math.ceil(v / 10) * 10;

//   valor: o que ofereceram (R$) · km: distância carregado
//   voltaVazia: a volta sem carga entra no custo (a mesma distância)
//   custo: { porKm: { diesel, viagem, reserva }, pctDoFrete } (de
//     custoDaViagem(); porKm null ou custo null: sem o custo ainda)
//   tabela, tipo, eixos: para o piso da ANTT
//   margem: quanto quer que sobre, para os fixos e o lucro (0,30 = 30%)
//   complementos: [{ valor, kmExtra }] cargas menores na mesma rota (0.20.0):
//     o valor soma à oferta e os km a mais (desvio) entram só na ida. O piso
//     é o da carga principal; o valor para pedir também (já contando os
//     complementos).
export function raioX({ valor, km, voltaVazia = true, custo = null, tabela = 'A', tipo, eixos, margem = null, complementos = [] }) {
  const distancia = Number(km) || 0;
  const oferta = Number(valor) || 0;
  const somaDosComplementos = complementos.reduce((s, c) => s + (Number(c.valor) || 0), 0);
  const kmExtra = complementos.reduce((s, c) => s + (Number(c.kmExtra) || 0), 0);
  const total = oferta + somaDosComplementos;
  const kmRodados = distancia * (voltaVazia ? 2 : 1) + kmExtra;
  const piso = pisoMinimo({ tabela, tipo, eixos, km: distancia });
  const r = {
    km: distancia,
    kmRodados,
    kmExtra,
    valor: oferta,
    complementos: reais(somaDosComplementos),
    total: reais(total),
    porKm: distancia > 0 && total > 0 ? reais(total / (distancia + kmExtra)) : null,
    piso,
    abaixoDoPiso: piso !== null && oferta > 0 && oferta < piso,
    custo: null,
    partes: null,
    sobra: null,
    margem: null,
    sugerido: null,
    motivoDoSugerido: null,
  };
  if (custo?.porKm && distancia > 0) {
    const pct = custo.pctDoFrete ?? 0;
    r.partes = {
      diesel: reais(custo.porKm.diesel * kmRodados),
      viagem: reais(custo.porKm.viagem * kmRodados),
      reserva: reais(custo.porKm.reserva * kmRodados),
      motorista: reais(pct * total),
    };
    const daViagem = (custo.porKm.diesel + custo.porKm.viagem + custo.porKm.reserva) * kmRodados;
    // A soma das linhas que aparecem (cada uma já arredondada): a conta fecha
    // no centavo para quem confere.
    r.custo = reais(r.partes.diesel + r.partes.viagem + r.partes.reserva + r.partes.motorista);
    if (total > 0) {
      r.sobra = reais(total - r.custo);
      r.margem = r.sobra / total;
    }
    if (margem !== null && margem >= 0 && pct + margem < 0.95) {
      // O total que dá a sobra escolhida, menos o que os complementos já pagam
      const pelaMargem = daViagem / (1 - pct - margem) - somaDosComplementos;
      r.sugerido = paraCima(Math.max(pelaMargem, piso ?? 0, 0));
      r.motivoDoSugerido = piso !== null && piso >= pelaMargem ? 'piso' : 'margem';
    }
  } else if (piso !== null) {
    r.sugerido = paraCima(piso);
    r.motivoDoSugerido = 'piso';
  }
  return r;
}

// O que a empresa já recebeu nessa rota (mesma cidade e UF de origem e de destino).
export function historicoDaRota(fretes, { origemCidade, origemUf, destinoCidade, destinoUf }) {
  const o = chaveDoNome(origemCidade);
  const d = chaveDoNome(destinoCidade);
  const lista = (fretes ?? [])
    .filter((f) => !f.deleted_at && f.valor_frete > 0
      && chaveDoNome(f.origem_cidade) === o && f.origem_uf === origemUf
      && chaveDoNome(f.destino_cidade) === d && f.destino_uf === destinoUf)
    .sort((a, b) => String(b.data_carregamento).localeCompare(String(a.data_carregamento)));
  if (lista.length === 0) return null;
  const valores = lista.map((f) => Number(f.valor_frete));
  return {
    cargas: lista.length,
    media: reais(valores.reduce((s, v) => s + v, 0) / valores.length),
    menor: Math.min(...valores),
    maior: Math.max(...valores),
    ultima: { data: lista[0].data_carregamento, valor: Number(lista[0].valor_frete) },
  };
}

// A mensagem com a contraproposta, para copiar no WhatsApp.
export function mensagemDaContraproposta({ origem, destino, km, valor, piso }) {
  const linhas = [
    `Olá! Sobre o frete de ${origem} para ${destino}${km ? ` (${numeroBR(Math.round(km))} km)` : ''}:`,
    `consigo fazer por ${dinheiro(valor)}.`,
  ];
  if (piso) linhas.push(`O piso mínimo da ANTT para essa viagem é ${dinheiro(piso)}.`);
  linhas.push('Fico no aguardo.');
  return linhas.join(' ');
}
